import { Clear, Search } from '@mui/icons-material';
import {
  Box,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ChangeEvent, memo, useCallback } from 'react';

import { translationLib } from 'entities/translation';

import { EEntityType, ETranslationStatus, LanguageListItem } from 'shared/api';

type Props = {
  entityType: EEntityType;
  language: string;
  status: ETranslationStatus;
  filter: string;
  languages: LanguageListItem[];
  totalCount: number;
  onEntityTypeChange: (type: EEntityType) => void;
  onLanguageChange: (lang: string) => void;
  onStatusChange: (status: ETranslationStatus) => void;
  onFilterChange: (value: string) => void;
};

export const TranslationFilters = memo(
  ({
    entityType,
    language,
    status,
    filter,
    languages,
    totalCount,
    onEntityTypeChange,
    onLanguageChange,
    onStatusChange,
    onFilterChange,
  }: Props) => {
    const handleSearchChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => onFilterChange(e.target.value),
      [onFilterChange],
    );

    const handleClear = useCallback(() => onFilterChange(''), [onFilterChange]);

    const noLanguages = languages.length === 0;

    return (
      <Paper sx={{ width: '100%', p: 2, mb: 1 }}>
        <Typography variant="h6" sx={{ mb: 1.5 }}>
          Translations editor
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <Select value={entityType} onChange={(e) => onEntityTypeChange(e.target.value as EEntityType)} displayEmpty>
              {translationLib.ENTITY_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {translationLib.ENTITY_TYPE_LABEL[type]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Tooltip title={noLanguages ? 'No languages with translations yet' : ''} disableHoverListener={!noLanguages}>
            <FormControl size="small" sx={{ minWidth: 200 }} disabled={noLanguages}>
              <Select
                value={noLanguages ? '' : language}
                displayEmpty
                onChange={(e) => onLanguageChange(e.target.value)}
              >
                {noLanguages ? (
                  <MenuItem value="" disabled>
                    No languages
                  </MenuItem>
                ) : (
                  languages.map((lang) => (
                    <MenuItem key={lang.code} value={lang.code}>
                      {lang.name} ({lang.code})
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Tooltip>

          <RadioGroup row value={status} onChange={(e) => onStatusChange(e.target.value as ETranslationStatus)}>
            <FormControlLabel value={ETranslationStatus.All} control={<Radio size="small" />} label="All" />
            <FormControlLabel value={ETranslationStatus.Missing} control={<Radio size="small" />} label="Missing" />
            <FormControlLabel
              value={ETranslationStatus.Translated}
              control={<Radio size="small" />}
              label="Translated"
            />
          </RadioGroup>

          <TextField
            size="small"
            placeholder="Search by code or EN name"
            value={filter}
            onChange={handleSearchChange}
            sx={{ flexGrow: 1, minWidth: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: filter ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClear} aria-label="clear search">
                    <Clear fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          <Typography variant="body2" color="text.secondary">
            {totalCount} {totalCount === 1 ? 'entity' : 'entities'}
          </Typography>
        </Box>
      </Paper>
    );
  },
);

TranslationFilters.displayName = 'TranslationFilters';
